import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { ConnectComponentsProvider, ConnectTerminalHardwareShop } from '@stripe/react-connect-js';
import { useAuthStore } from 'stores/auth.store';
import driverServices from 'services/driver.services';

// Mapped from the Falcon theme tokens the rest of the portal uses, so the embedded component does not
// look like a foreign iframe.
const CONNECT_APPEARANCE = {
  variables: {
    colorPrimary: '#2c7be5',
    colorText: '#5e6e82',
    colorBackground: '#ffffff',
    colorDanger: '#e63757',
    borderRadius: '4px',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  }
};

const STATUS_LOADING = 'loading';
const STATUS_READY = 'ready';
const STATUS_ERROR = 'error';

export default function TerminalHardwareShop() {
  const { userData } = useAuthStore();
  const region = userData?.stripeOnboardingBasedCountry;

  const [connectInstance, setConnectInstance] = useState(null);
  const [status, setStatus] = useState(STATUS_LOADING);
  // Guards against React re-running the mount effect and building a second Connect instance.
  const initialisedRef = useRef(false);

  const initialise = useCallback(async () => {
    setStatus(STATUS_LOADING);

    // Without a region the request would carry an empty x-region header, which the router silently
    // treats as AU — a US or GB user would then transact against the wrong platform account.
    if (!region) {
      setStatus(STATUS_ERROR);
      return;
    }

    try {
      // The publishable key is needed before the component can mount, and only the endpoint knows
      // which of the three platform accounts this user belongs to. The axios response interceptor
      // already unwraps to the body, which carries publishable_key/client_secret at the top level.
      const session = await driverServices.getTerminalHardwareAccountSession(region);

      const instance = loadConnectAndInitialize({
        publishableKey: session.publishable_key,
        // Never memoise this result: connect-js re-invokes the callback when the session expires and
        // must receive a freshly issued secret each time.
        fetchClientSecret: async () => {
          const response = await driverServices.getTerminalHardwareAccountSession(region);
          return response.client_secret;
        },
        appearance: CONNECT_APPEARANCE
      });

      setConnectInstance(instance);
      setStatus(STATUS_READY);
    } catch (error) {
      // Surface the real failure (HTTP status / body / shape mismatch) instead of swallowing it,
      // otherwise the generic error card gives no way to tell a 404/401/500 apart from a bad payload.
      // eslint-disable-next-line no-console
      console.error('[TerminalHardwareShop] failed to open account session:', error);
      setStatus(STATUS_ERROR);
    }
  }, [region]);

  useEffect(() => {
    if (initialisedRef.current) return;
    initialisedRef.current = true;
    initialise();
  }, [initialise]);

  const onRetry = () => {
    setConnectInstance(null);
    initialise();
  };

  const onCheckoutFinished = () => {
    toast.success('Your terminal hardware order has been placed.');
  };

  const onLoadError = () => {
    setStatus(STATUS_ERROR);
  };

  return (
    <Card>
      <Card.Header>
        <h5 className="mb-0">Terminal Hardware Shop</h5>
      </Card.Header>
      <Card.Body>
        {status === STATUS_LOADING && (
          <div className="text-center py-6">
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {status === STATUS_ERROR && (
          <div className="text-center py-6">
            <p className="mb-3">
              We could not open the terminal hardware shop. Please try again in a moment.
            </p>
            <Button variant="primary" onClick={onRetry}>
              Retry
            </Button>
          </div>
        )}

        {status === STATUS_READY && connectInstance && (
          <ConnectComponentsProvider connectInstance={connectInstance}>
            <ConnectTerminalHardwareShop
              onCheckoutFinished={onCheckoutFinished}
              onLoadError={onLoadError}
            />
          </ConnectComponentsProvider>
        )}
      </Card.Body>
    </Card>
  );
}
